/**
 * IK target state only. The solver itself lives in the viewport (wave 3): it reads these
 * targets, solves, and writes the result back through `setLivePoseBone` for the root and mid
 * bones — so the clip never learns that IK existed (plan D22, SPEC serialization rule).
 */

import type { IkChainName, Vec3Tuple } from '../../shared';
import type { IkTargetState } from '../types';
import type { ActionContext } from './context';

export interface IkActions {
  setIkEnabled(enabled: boolean): void;
  setIkTarget(chain: IkChainName, position: Vec3Tuple, pole?: Vec3Tuple): void;
  activateIk(chain: IkChainName, active: boolean): void;
  beginIkDrag(chain: IkChainName): void;
  endIkDrag(): void;
}

function vec(v: Vec3Tuple): Vec3Tuple {
  return [v[0], v[1], v[2]];
}

/**
 * IK activation is momentary (wave-5 decision): any user-driven playhead change hands the limbs
 * back to the clip, so every handle re-glues to its end bone instead of dragging the pose towards
 * a target that was placed at some other frame.
 *
 * Not undoable — activation is transient UI state, exactly like `activateIk` itself. Exported so
 * the clip actions (`setPlayhead`, `stop`, `openClip`, `newClip`) can run it as an in-action side
 * effect; nothing dispatches from a reducer.
 */
export function deactivateAllIk(ctx: ActionContext): void {
  const ik = ctx.getState().ik;
  const names = Object.keys(ik.targets) as IkChainName[];
  let changed = false;
  const targets = { ...ik.targets };
  for (const name of names) {
    const target = targets[name];
    if (!target?.active) continue;
    targets[name] = { ...target, active: false };
    changed = true;
  }
  if (!changed) return;
  ctx.set({ ik: { ...ik, targets } });
}

export function createIkActions(ctx: ActionContext): IkActions {
  const writeTarget = (chain: IkChainName, target: IkTargetState | undefined): void => {
    const ik = ctx.getState().ik;
    const targets = { ...ik.targets };
    if (target) targets[chain] = target;
    else delete targets[chain];
    ctx.set({ ik: { ...ik, targets } });
  };

  return {
    setIkEnabled(enabled): void {
      const ik = ctx.getState().ik;
      if (ik.enabled === enabled) return;
      ctx.set({ ik: { ...ik, enabled } });
    },

    setIkTarget(chain, position, pole): void {
      const previous = ctx.getState().ik.targets[chain];
      const nextPole = pole ?? previous?.pole;
      const next: IkTargetState = {
        position: vec(position),
        active: previous?.active ?? false,
      };
      if (nextPole) next.pole = vec(nextPole);

      writeTarget(chain, next);
      ctx.history.push({
        label: `Move ${chain} IK target`,
        key: `ik:${chain}`,
        slice: `ik:${chain}`,
        undo: () => writeTarget(chain, previous),
        redo: () => writeTarget(chain, next),
      });
    },

    activateIk(chain, active): void {
      const target = ctx.getState().ik.targets[chain];
      // Nothing to activate until the viewport has placed a target for this chain.
      if (!target || target.active === active) return;
      writeTarget(chain, { ...target, active });
    },

    beginIkDrag(chain): void {
      ctx.history.beginCoalesce(`Move ${chain} IK target`, `ik:${chain}`);
    },

    endIkDrag(): void {
      ctx.history.endCoalesce();
    },
  };
}
